import {useCallback, useEffect, useRef, useState} from 'react'
import {Dialog, Spinner} from '@primer/react'
import {StackIcon} from '@primer/octicons-react'
import {StackOverlay} from '@github-ui/pull-requests/components/StackOverlay'
import {PageDataContextProvider} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import styles from './StackBadge.module.css'
import {useStackData} from './use-stack-data'

interface StackBadgeProps {
  pullRequestNumber: number
  stackPosition: number | null
  stackSize: number | null
  repoNameWithOwner: string
  includeDot?: boolean
}

interface StackOverlayDialogProps {
  repoNameWithOwner: string
  pullRequestNumber: number
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  onClose: () => void
}

function StackOverlayDialog({repoNameWithOwner, pullRequestNumber, returnFocusRef, onClose}: StackOverlayDialogProps) {
  const {data, isLoading, error} = useStackData(repoNameWithOwner, pullRequestNumber)
  const stack = data?.stack ?? null
  const currentEntryRef = useRef<HTMLAnchorElement>(null)

  // The dialog's focus trap runs before the stack data finishes loading, so it can't use the
  // current entry as its initialFocusRef target. Focus it once it renders to scroll the current entry into view.
  useEffect(() => {
    if (stack) currentEntryRef.current?.focus()
  }, [stack])

  return (
    <Dialog
      title={`Stack ${stack ? `#${stack.number}` : ''}`}
      width="large"
      returnFocusRef={returnFocusRef}
      initialFocusRef={currentEntryRef}
      onClose={onClose}
      renderBody={({children}) => <Dialog.Body className="p-0">{children}</Dialog.Body>}
    >
      {isLoading && (
        <div className="d-flex flex-justify-center flex-items-center p-4">
          <Spinner size="medium" />
        </div>
      )}
      {error && (
        <div className="d-flex flex-justify-center flex-items-center p-4">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <p className="text-center color-text-danger">Failed to load stack data.</p>
        </div>
      )}
      {stack && (
        // StackOverlay reads from the PageDataContextProvider (for its feedback URL);
        // the /pulls dashboard doesn't use this context elsewhere, so we supply the current PR's base URL here.
        <PageDataContextProvider basePageDataUrl={`/${repoNameWithOwner}/pull/${pullRequestNumber}`}>
          <StackOverlay
            stack={stack}
            pullRequestNumber={pullRequestNumber}
            baseBranchText={stack.baseBranch}
            showStackMutationButtons={false}
            currentEntryRef={currentEntryRef}
          />
        </PageDataContextProvider>
      )}
    </Dialog>
  )
}

export function StackBadge({
  pullRequestNumber,
  stackPosition,
  stackSize,
  repoNameWithOwner,
  includeDot = true,
}: StackBadgeProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const badgeRef = useRef<HTMLButtonElement>(null)

  const handleClose = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  if (stackPosition === null || stackSize === null || stackPosition === undefined || stackSize === undefined) {
    return null
  }

  return (
    <>
      {includeDot && <span aria-hidden="true"> &middot; </span>}
      <button
        type="button"
        className={styles.badge}
        onClick={() => setIsDialogOpen(true)}
        ref={badgeRef}
        aria-haspopup="dialog"
        aria-expanded={isDialogOpen}
        aria-label={`Pull request stack, position ${stackPosition} of ${stackSize}`}
      >
        <StackIcon size={16} />
        {stackPosition}/{stackSize}
      </button>
      {isDialogOpen && (
        <StackOverlayDialog
          repoNameWithOwner={repoNameWithOwner}
          pullRequestNumber={pullRequestNumber}
          returnFocusRef={badgeRef}
          onClose={handleClose}
        />
      )}
    </>
  )
}
